// RolePermissionsList.jsx
import {
    Add as AddIcon,
    Clear as ClearIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    Snackbar,
    TextField,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { deleteRole, fetchRoles, resetCurrentRole } from '../../redux/role/roleAction';

const RolesList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [rolesData, setRolesData] = useState([]);

  // Redux state
  const roleState = useSelector((state) => state?.role?.fetchRoles || {});
  const deleteRoleState = useSelector((state) => state?.role?.deleteRole || {});

  useEffect(() => {
    dispatch(fetchRoles(currentPage));
  }, [dispatch, currentPage]);

  // Handle delete role success/error
  useEffect(() => {
    if (deleteRoleState.success) {
      setSnackbarMessage('Role deleted successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      dispatch(fetchRoles(currentPage));
      dispatch(resetCurrentRole());
      setDeleteDialogOpen(false);
    } else if (deleteRoleState.error) {
      setSnackbarMessage(`Failed to delete role: ${deleteRoleState.error}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      dispatch(resetCurrentRole());
    }
  }, [deleteRoleState, dispatch, currentPage]);

  // Process roles data
  useEffect(() => {
    if (roleState.success && roleState.Roles && roleState.Roles.data) {
      setRolesData(roleState.Roles.data);
    }
  }, [roleState]);

  // Filter roles based on search
  const filteredRoles = rolesData.filter(role =>
    role.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleAddRole = () => {
    navigate('/role-permissions/add');
  };

  const handleEditRole = (roleId) => {
    navigate(`/role-permissions/edit/${roleId}`);
  };

  const handleDeleteClick = (role) => {
    setSelectedRole(role);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedRole) {
      dispatch(deleteRole(selectedRole.id));
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedRole(null);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const getPermissionSummary = (permissions) => {
    if (!permissions) return 'No permissions';
    
    try {
      const parsedPermissions = JSON.parse(permissions);
      const moduleCount = Object.keys(parsedPermissions).length;
      return `${moduleCount} modules configured`;
    } catch (error) {
      return 'Invalid permissions';
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
          Role Permissions Management
        </Typography>
        
        {/* Search and Actions */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search roles..."
              value={searchQuery}
              onChange={handleSearchChange}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="primary" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setSearchQuery('')} size="small">
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddRole}
              fullWidth
              size="large"
              sx={{ 
                backgroundColor: '#4CAF50',
                '&:hover': { backgroundColor: '#45a049' }
              }}
            >
              Add New Role
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Roles List */}
      <Grid container spacing={3}>
        {filteredRoles.length === 0 ? (
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
              <Typography variant="h6" color="textSecondary">
                {searchQuery ? 'No roles found matching your search' : 'No roles available'}
              </Typography>
            </Paper>
          </Grid>
        ) : (
          filteredRoles.map((role, index) => (
            <Grid item xs={12} sm={6} md={4} key={role.id}>
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 3, 
                  borderRadius: 2,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                  }
                }}
              >
                {/* Role Header */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {role.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Serial: {role.serial_no || index + 1}
                  </Typography>
                </Box>

                {/* Role Info */}
                <Box sx={{ flexGrow: 1, mb: 2 }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Status: {role.status === '1' ? 'Active' : 'Inactive'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Permissions: {getPermissionSummary(role.permissions)}
                  </Typography>
                </Box>

                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditRole(role.id)}
                    sx={{ 
                      borderColor: '#FF9800',
                      color: '#FF9800',
                      '&:hover': {
                        backgroundColor: '#FF9800',
                        color: 'white'
                      }
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDeleteClick(role)}
                    sx={{ 
                      borderColor: '#f44336',
                      color: '#f44336',
                      '&:hover': {
                        backgroundColor: '#f44336',
                        color: 'white'
                      }
                    }}
                  >
                    Delete
                  </Button>
                </Box>
              </Paper>
            </Grid>
          ))
        )}
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the role "{selectedRole?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity} 
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RolesList;